/**
 * Small helpers shared by the LOP Loans admin pages (settings, splash notices, access and roles). LOP-owned.
 */

import { LoansApiError } from './loansApi';
import { errorText } from './writeups/common';

/** The API's own sentence for a modal, without the developer code suffix. */
export function modalError(e: unknown): string {
  if (e instanceof LoansApiError) return e.message ? e.message.charAt(0).toUpperCase() + e.message.slice(1) : errorText(e);
  return errorText(e);
}

const CENTRAL = 'America/Chicago';

/** Central wall-clock parts of an instant. */
function centralParts(ms: number): { y: number; mo: number; d: number; h: number; mi: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CENTRAL,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute') };
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** An ISO instant as the value of a datetime-local input, in Central time. */
export function utcToCentralInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(ms)) return '';
  const p = centralParts(ms);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** A datetime-local value read as Central time, as a UTC ISO string (daylight saving handled); null when not a date. */
export function centralInputToUtcIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local.trim());
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Central is UTC-6 or UTC-5: start from -6, then correct by what Central actually reads at that instant (twice, for the switch).
  let guess = wall + 6 * 3600_000;
  for (let i = 0; i < 2; i += 1) {
    const p = centralParts(guess);
    const seen = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
    guess += wall - seen;
  }
  return new Date(guess).toISOString();
}

/** "now" as a datetime-local value in Central time, rounded down to the minute. */
export function nowCentralInput(offsetMinutes = 0): string {
  return utcToCentralInput(new Date(Date.now() + offsetMinutes * 60_000).toISOString());
}
